import React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Button } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useLanguage } from '../contexts/LanguageContext';
import en from '../locales/en';
import zh from '../locales/zh';

const Container = styled.div`
  min-height: calc(100vh - 64px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background-color: #000;
  
  @media (max-width: 768px) {
    padding: 16px;
    min-height: calc(100vh - 56px);
  }
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 40px;
  border-radius: 16px;
  width: 100%;
  max-width: 400px;
  backdrop-filter: blur(10px);
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 24px;
    max-width: 100%;
  }
  
  @media (max-width: 480px) {
    padding: 20px;
    border-radius: 12px;
  }
`;

const IconWrapper = styled.div`
  font-size: 64px;
  margin-bottom: 24px;
  
  .success-icon {
    color: #52c41a;
  }
  
  .error-icon {
    color: #ff4d4f;
  }
  
  .cancel-icon {
    color: #faad14;
  }
  
  @media (max-width: 480px) {
    font-size: 48px;
    margin-bottom: 20px;
  }
`;

const Title = styled.h1`
  font-size: 24px;
  color: #fff;
  margin-bottom: 16px;
  
  @media (max-width: 480px) {
    font-size: 20px;
    margin-bottom: 12px;
  }
`;

const Description = styled.p`
  font-size: 16px;
  color: #888;
  margin-bottom: 32px;
  line-height: 1.5;
  
  @media (max-width: 480px) {
    font-size: 14px;
    margin-bottom: 24px;
  }
`;

const ActionButton = styled(Button)`
  height: 40px;
  padding: 0 32px;
  font-size: 16px;
  border-radius: 20px;
  
  @media (max-width: 480px) {
    height: 36px;
    font-size: 14px;
  }
`;

function AppLink() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();
  const t = currentLanguage === 'en' ? en : zh;
  
  const status = searchParams.get('status');
  
  const getStatusConfig = () => {
    switch (status) {
      case 'success':
        return {
          icon: <CheckCircleOutlined className="success-icon" />,
          title: t.payment.success.title,
          description: t.payment.success.description
        };
      case 'cancel':
        return {
          icon: <ExclamationCircleOutlined className="cancel-icon" />,
          title: t.payment.cancel.title,
          description: t.payment.cancel.description
        };
      case 'error':
      default:
        return {
          icon: <CloseCircleOutlined className="error-icon" />,
          title: t.payment.error.title,
          description: t.payment.error.description
        };
    }
  };
  
  const statusConfig = getStatusConfig();
  
  return (
    <Container>
      <Card>
        <IconWrapper>
          {statusConfig.icon}
        </IconWrapper>
        <Title>{statusConfig.title}</Title>
        <Description>{statusConfig.description}</Description>
      </Card>
    </Container>
  );
}

export default AppLink;